/**
 * Embedded PDF viewer for platform agreements (presigned S3 GET URLs).
 * Uses react-pdf / pdf.js so every open starts at page 1 (native iframe viewers
 * honor PDF OpenAction and cache scroll position by URL).
 *
 * Text/annotation layers stay off: PyMuPDF-personalized firm drafts can terminate
 * the pdf.js worker during getTextContent (blank viewer + sendWithStream errors
 * when the parent re-renders, e.g. typing in the e-sign form below the preview).
 * @viewport both
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TOUCH, ZINDEX } from '@/core/platform/platformStandards';
import { ensureAgreementPdfWorker } from '@/components/agreements/agreementPdfSetup';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

ensureAgreementPdfWorker();

/** Spinner + compact caption for agreement personalize / sign waits. */
export function AgreementBusyState({
  message,
  minHeight = { xs: 'min(58vh, 420px)', sm: 480 },
  size = 28,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1.5,
        minHeight,
        px: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <CircularProgress size={size} aria-label={message || 'Loading'} />
      {message ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center', maxWidth: 320, lineHeight: 1.45 }}
        >
          {message}
        </Typography>
      ) : null}
    </Box>
  );
}

AgreementBusyState.propTypes = {
  message: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  size: PropTypes.number,
};

AgreementBusyState.defaultProps = {
  message: '',
  minHeight: { xs: 'min(58vh, 420px)', sm: 480 },
  size: 28,
};

/** Presigned URLs keep the query string; #page=N is client-only and does not break S3 signing. */
export function pdfEmbedSrc(pdfUrl, page = 1) {
  if (!pdfUrl) {
    return '';
  }
  const base = pdfUrl.split('#')[0];
  return `${base}#page=${page}&view=Fit`;
}

export function openAgreementPdfInNewTab(pdfUrl, page = 1) {
  if (!pdfUrl) {
    return;
  }
  window.open(pdfEmbedSrc(pdfUrl, page), '_blank', 'noopener,noreferrer');
}

const AgreementPdfViewer = ({
  pdfUrl,
  isLoading,
  error,
  title: _title = 'Agreement document',
  minHeight = { xs: 'min(58vh, 420px)', sm: 480 },
  showOpenInNewTab = false,
  showHint = false,
  startPage = 1,
  loadingMessage = '',
  iframeLoadingMessage = 'Opening document…',
  viewerInstanceKey = 0,
  active = true,
  renderTextLayer = false,
  renderAnnotationLayer = false,
}) => {
  const scrollRef = useRef(null);
  const measureRef = useRef(null);
  const [pageWidth, setPageWidth] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState('');

  // Tracks which document version (url + instanceKey) has actually finished
  // loading. Page children are only rendered when this matches the current
  // version, preventing the one-render-cycle race where pagesToRender still
  // holds the previous numPages while the new Document context is still null
  // (which causes pdf.js to crash with "sendWithPromise" / "sendWithStream").
  const [loadedVersion, setLoadedVersion] = useState(null);

  // Stable key derived synchronously — used for both <Document key> and the
  // version guard. Changes immediately when pdfUrl / viewerInstanceKey changes.
  const documentVersion = useMemo(
    () => `${viewerInstanceKey}::${pdfUrl || ''}`,
    [viewerInstanceKey, pdfUrl]
  );

  const showDocument = active && !isLoading && !error && Boolean(pdfUrl);

  useEffect(() => {
    setLoadedVersion(null);
    if (!showDocument) {
      setNumPages(null);
      setDocumentError('');
      setDocumentLoading(false);
      return;
    }
    setDocumentLoading(true);
    setDocumentError('');
    setNumPages(null);
  }, [showDocument, pdfUrl, viewerInstanceKey]);

  useEffect(() => {
    if (!showDocument || !scrollRef.current) {
      return;
    }
    scrollRef.current.scrollTop = 0;
  }, [showDocument, pdfUrl, viewerInstanceKey, startPage]);

  useEffect(() => {
    const node = measureRef.current;
    if (!node || !showDocument) {
      return undefined;
    }

    const updateWidth = () => {
      setPageWidth(Math.max(node.clientWidth - 2, 240));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showDocument]);

  // documentVersion in deps so each new load records the version it belongs to.
  const handleDocumentLoadSuccess = useCallback(
    ({ numPages: totalPages }) => {
      setNumPages(totalPages);
      setLoadedVersion(documentVersion);
      setDocumentLoading(false);
      setDocumentError('');
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    },
    [documentVersion]
  );

  const handleDocumentLoadError = useCallback((loadError) => {
    console.error('Agreement PDF load failed:', loadError);
    setDocumentLoading(false);
    setDocumentError(
      loadError?.message?.includes('Failed to fetch')
        ? 'Could not load the agreement PDF. If this persists, use Open in new tab or contact support.'
        : loadError?.message || 'Failed to load agreement PDF.'
    );
  }, []);

  const pagesToRender = useMemo(
    () =>
      numPages != null ? Array.from({ length: numPages }, (_, index) => index + 1) : [],
    [numPages]
  );

  // Only true once the currently-active document version has finished loading.
  // Prevents Page components from being rendered under a still-loading Document.
  const documentReady = loadedVersion === documentVersion && pagesToRender.length > 0;

  // Stable reference — react-pdf v9 uses options as a load dep; a new object
  // on every render re-triggers the full document load (the blink on typing).
  const documentOptions = useMemo(() => ({ withCredentials: false }), []);

  if (!active) {
    return null;
  }

  if (isLoading) {
    return (
      <AgreementBusyState message={loadingMessage || 'Loading agreement…'} minHeight={minHeight} />
    );
  }

  if (error || !pdfUrl) {
    return (
      <Alert severity="error" sx={{ minHeight: typeof minHeight === 'object' ? minHeight.sm : minHeight }}>
        {error || 'Agreement PDF is not available. Check the API response and S3 configuration.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: showHint ? 1 : 0, minHeight: 0, height: '100%' }}>
      {showOpenInNewTab && (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => openAgreementPdfInNewTab(pdfUrl, startPage)}
            sx={{ minHeight: TOUCH.minSize }}
          >
            Open in new tab
          </Button>
        </Stack>
      )}

      <Box
        ref={measureRef}
        sx={{
          position: 'relative',
          flex: 1,
          minHeight,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {(documentLoading || !pageWidth) && !documentError && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              zIndex: ZINDEX.raised,
              gap: 1,
              px: 2,
            }}
          >
            <CircularProgress size={28} />
            {iframeLoadingMessage ? (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {iframeLoadingMessage}
              </Typography>
            ) : null}
          </Box>
        )}

        {documentError ? (
          <Box sx={{ p: 2 }}>
            <Alert
              severity="error"
              action={
                <Button
                  size="small"
                  onClick={() => openAgreementPdfInNewTab(pdfUrl, startPage)}
                  sx={{ minHeight: TOUCH.minSize }}
                >
                  Open in new tab
                </Button>
              }
            >
              {documentError}
            </Alert>
          </Box>
        ) : (
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              px: { xs: 0.5, sm: 1 },
              py: 1,
            }}
          >
            <Document
              key={documentVersion}
              file={pdfUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading=""
              error=""
              options={documentOptions}
            >
              {pageWidth &&
                documentReady &&
                pagesToRender.map((pageNumber) => (
                  <Box
                    key={`page-${pageNumber}-${viewerInstanceKey}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 1.5,
                      '& .react-pdf__Page': { maxWidth: '100%' },
                      '& .react-pdf__Page__canvas': { maxWidth: '100%', height: 'auto !important' },
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={pageWidth}
                      renderTextLayer={renderTextLayer}
                      renderAnnotationLayer={renderAnnotationLayer}
                    />
                  </Box>
                ))}
            </Document>
          </Box>
        )}
      </Box>

      {showHint && (
        <Typography variant="caption" color="text.secondary">
          Use &ldquo;Open in new tab&rdquo; for print or zoom if the preview is hard to read on your device.
        </Typography>
      )}
    </Box>
  );
};

AgreementPdfViewer.propTypes = {
  pdfUrl: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  title: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string]),
  showOpenInNewTab: PropTypes.bool,
  showHint: PropTypes.bool,
  startPage: PropTypes.number,
  loadingMessage: PropTypes.string,
  iframeLoadingMessage: PropTypes.string,
  viewerInstanceKey: PropTypes.number,
  active: PropTypes.bool,
  renderTextLayer: PropTypes.bool,
  renderAnnotationLayer: PropTypes.bool,
};

AgreementPdfViewer.defaultProps = {
  pdfUrl: null,
  isLoading: false,
  error: '',
  title: 'Agreement document',
  minHeight: { xs: 'min(58vh, 420px)', sm: 480 },
  showOpenInNewTab: false,
  showHint: false,
  startPage: 1,
  loadingMessage: '',
  iframeLoadingMessage: 'Opening document…',
  viewerInstanceKey: 0,
  active: true,
  renderTextLayer: false,
  renderAnnotationLayer: false,
};

export default AgreementPdfViewer;
